/**
 * SyntheticSymbolPanes — the two panes BELOW the Charts symbol pane
 * (docs/SYNTHETIC-SYMBOLS-PLAN.md §5):
 *   Pane 1 "Live"  — synthetics with an open position (any chip type).
 *   Pane 2 "Auto"  — the 4 directional chips × D0/D1/D2/W0/M0 buckets for the
 *                    SELECTED underlying, with a Refresh that calls the same
 *                    recompute routine as the day-rollover cron.
 * Plus a section for the user's MANUAL saved (not yet live) synthetics.
 *
 * Self-hides when the server feature flag is off (list → 403) so the Charts
 * page is unchanged in production until SYNTHETIC_SYMBOLS_ENABLED=true.
 * Right-click a row → Delete (blocked while LIVE) / Disarm trigger.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CH_TYPE } from '../../styles/appUi';
import { Box, Typography, IconButton, Tooltip, Menu, MenuItem, CircularProgress, Chip, Collapse } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import toast from 'react-hot-toast';
import { syntheticSymbolsService, syntheticColor, SyntheticSymbolRow } from '../../services/syntheticSymbols.service';

/** Within-bucket presentation order (user spec): Strangle, Straddle, Db_L, Cr_L, Db_S, Cr_S. */
const autoOrder = (r: SyntheticSymbolRow): number => {
  if (r.chipType === 'STRANGLE') return 0;
  if (r.chipType === 'STRADDLE') return 1;
  const up = r.viewAligned === 'UP', credit = !!r.isCredit;
  if (!credit && up) return 2;   // _Db_L_
  if (credit && up) return 3;    // _Cr_L_
  if (!credit && !up && r.viewAligned === 'DOWN') return 4;  // _Db_S_
  if (credit && r.viewAligned === 'DOWN') return 5;          // _Cr_S_
  return 6;
};
const BUCKETS: Array<'D0' | 'D1' | 'D2' | 'W0' | 'M0'> = ['D0', 'D1', 'D2', 'W0', 'M0'];
const AUTO_COLLAPSE_KEY = 'synthAutoCollapse_v1';
const SCANNER_COLLAPSE_KEY = 'synthScannerCollapse_v1';

interface Props {
  /** Base underlying of the currently selected chart symbol (e.g. 'BTC', 'NIFTY');
   *  '' when unknown — Pane 2 then shows a hint instead of rows. */
  underlyingSymbol: string;
  /** Currently open synthetic id (row highlight). */
  activeSyntheticId: number | null;
  /** Open a synthetic's combined chart. */
  onOpen: (row: SyntheticSymbolRow) => void;
  /** Bumped by the parent when a synthetic changes elsewhere (save/arm/close). */
  refreshNonce: number;
}

const Row: React.FC<{
  row: SyntheticSymbolRow; active: boolean;
  onClick: () => void; onContext: (e: React.MouseEvent, row: SyntheticSymbolRow) => void;
  /** Inline "−" remove (no right-click needed). Hidden while LIVE. */
  onRemove?: (row: SyntheticSymbolRow) => void;
}> = ({ row, active, onClick, onContext, onRemove }) => {
  const color = syntheticColor(row);
  const statusTag = row.status === 'ARMED' ? '⏳' : row.status === 'LIVE' ? '●' : row.status === 'LIVE_PARTIAL' ? '◐' : '';
  const live = row.status === 'LIVE' || row.status === 'LIVE_PARTIAL';
  return (
    <Box
      onClick={onClick}
      onContextMenu={e => { e.preventDefault(); onContext(e, row); }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.6, px: 0.8, py: 0.28, cursor: 'pointer',
        borderLeft: `3px solid ${color}`,
        bgcolor: active ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
        '&:hover .synth-remove': { opacity: 1 },
      }}>
      {/* Full name on hover — the pane is narrow and long names ellipsize. */}
      <Tooltip arrow title={row.name} enterDelay={400}>
        <Typography sx={{ fontSize: CH_TYPE.small, fontWeight: 700, color, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.name}
        </Typography>
      </Tooltip>
      {statusTag && (
        <Typography component="span" sx={{ fontSize: CH_TYPE.micro, color: row.status === 'LIVE_PARTIAL' ? '#ef6c00' : color }}>
          {statusTag}
        </Typography>
      )}
      <Chip size="small" label={row.isCredit ? 'Cr' : 'Db'}
        sx={{ height: 18, fontSize: CH_TYPE.micro, fontWeight: 800, bgcolor: 'transparent', border: `1px solid ${color}`, color, '& .MuiChip-label': { px: 0.4 } }} />
      {onRemove && !live && (
        <Tooltip arrow title={row.status === 'ARMED' ? 'Remove (disarms the trigger first)' : 'Remove this synthetic symbol'}>
          <IconButton
            size="small" className="synth-remove"
            onClick={e => { e.stopPropagation(); onRemove(row); }}
            sx={{ p: 0, opacity: 0.25, transition: 'opacity 120ms', color: 'text.secondary' }}>
            <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const SyntheticSymbolPanes: React.FC<Props> = ({ underlyingSymbol, activeSyntheticId, onOpen, refreshNonce }) => {
  const [enabled, setEnabled] = useState<boolean | null>(null); // null = probing
  const [live, setLive] = useState<SyntheticSymbolRow[]>([]);
  const [manual, setManual] = useState<SyntheticSymbolRow[]>([]);
  const [auto, setAuto] = useState<SyntheticSymbolRow[]>([]);
  const [scanRows, setScanRows] = useState<SyntheticSymbolRow[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [ctx, setCtx] = useState<{ anchor: { left: number; top: number }; row: SyntheticSymbolRow } | null>(null);

  const load = useCallback(async () => {
    const [l, m] = await Promise.all([
      syntheticSymbolsService.list('live'),
      syntheticSymbolsService.list('main'),
    ]);
    if (l.disabled || m.disabled) { setEnabled(false); return; }
    if (!l.ok && !m.ok) return; // transient — keep previous
    setEnabled(true);
    if (l.ok) setLive(l.rows);
    if (m.ok) setManual(m.rows);
    if (underlyingSymbol) {
      const a = await syntheticSymbolsService.list('auto', underlyingSymbol);
      if (a.ok) setAuto(a.rows);
    } else setAuto([]);
    // Bhairav_Scanner matches — cross-underlying (BTC+ETH), server-sorted ascending
    // by expiry so the soonest-expiring setups sit on top.
    const sc = await syntheticSymbolsService.list('scan');
    if (sc.ok) setScanRows(sc.rows);
  }, [underlyingSymbol]);

  useEffect(() => { void load(); }, [load, refreshNonce]);
  // Light poll so Pane 1 tracks trigger fires / closes without a manual refresh.
  useEffect(() => {
    if (enabled === false) return;
    const t = window.setInterval(() => { void load(); }, 30_000);
    return () => window.clearInterval(t);
  }, [enabled, load]);

  const doRefreshAuto = async () => {
    if (!underlyingSymbol || refreshing) return;
    setRefreshing(true);
    const r = await syntheticSymbolsService.refreshAuto(underlyingSymbol);
    setRefreshing(false);
    if (r.ok) { toast.success(`Auto synthetics refreshed (${r.created ?? 0} generated)`, { id: 'synth-refresh' }); void load(); }
    else toast.error(r.reason || 'Refresh failed', { id: 'synth-refresh' });
  };

  const onContext = (e: React.MouseEvent, row: SyntheticSymbolRow) =>
    setCtx({ anchor: { left: e.clientX, top: e.clientY }, row });

  const doDelete = async () => {
    if (!ctx) return;
    const r = await syntheticSymbolsService.remove(ctx.row.id);
    if (r.ok) { toast.success(`${ctx.row.name} deleted`); void load(); }
    else toast.error(r.reason || 'Delete failed');
    setCtx(null);
  };

  // Inline "−" remove — same delete as the context menu, no right-click and no
  // confirm (one-click removal; LIVE rows are already blocked server-side).
  const removeRow = useCallback(async (row: SyntheticSymbolRow) => {
    const r = await syntheticSymbolsService.remove(row.id);
    if (r.ok) { toast.success(`${row.name} removed`); void load(); }
    else toast.error(r.reason || 'Remove failed');
  }, [load]);

  // Saved-pane scope toggle: only the SELECTED underlying's saved synthetics (default)
  // vs all. Persisted per device.
  const [savedScoped, setSavedScoped] = useState<boolean>(() => {
    try { return localStorage.getItem('synthSavedScoped_v1') !== 'false'; } catch { return true; }
  });
  const toggleSavedScope = () => setSavedScoped(prev => {
    const next = !prev;
    try { localStorage.setItem('synthSavedScoped_v1', String(next)); } catch { /* ignore */ }
    return next;
  });

  // Collapsible D0/D1/D2/W0/M0 groups in the AUTO pane (persisted per device).
  const [autoCollapsed, setAutoCollapsed] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(AUTO_COLLAPSE_KEY) || '{}') || {}; } catch { return {}; }
  });
  const toggleBucket = (b: string) => setAutoCollapsed(prev => {
    const next = { ...prev, [b]: !prev[b] };
    try { localStorage.setItem(AUTO_COLLAPSE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  // Scanner section — collapsible (persisted) so a long, growing match list can't
  // overshadow the SAVED / LIVE / AUTO sections; its rows scroll inside a fixed cap.
  const [scannerCollapsed, setScannerCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(SCANNER_COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const toggleScanner = () => setScannerCollapsed(prev => {
    const next = !prev;
    try { localStorage.setItem(SCANNER_COLLAPSE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });
  const autoByBucket = useMemo(() => {
    const m = new Map<string, SyntheticSymbolRow[]>();
    for (const r of auto) {
      const b = String(r.expiryBucket || 'D0');
      if (!m.has(b)) m.set(b, []);
      m.get(b)!.push(r);
    }
    for (const rows of Array.from(m.values())) rows.sort((a, b) => autoOrder(a) - autoOrder(b) || a.name.localeCompare(b.name));
    return m;
  }, [auto]);
  const doDisarm = async () => {
    if (!ctx) return;
    const r = await syntheticSymbolsService.cancelTrigger(ctx.row.id);
    if (r.ok) { toast.success(`${ctx.row.name} trigger disarmed`); void load(); }
    else toast.error(r.reason || 'Disarm failed');
    setCtx(null);
  };

  if (enabled === false || enabled === null) return null; // feature off (or still probing) — render nothing

  const Section: React.FC<{ title: string; children: React.ReactNode; extra?: React.ReactNode }> = ({ title, children, extra }) => (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.4, mt: 0.4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 0.8 }}>
        <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 800, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase', flex: 1 }}>
          {title}
        </Typography>
        {extra}
      </Box>
      {children}
    </Box>
  );

  const empty = (msg: string) => (
    <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.disabled', px: 0.9, py: 0.3 }}>{msg}</Typography>
  );

  return (
    <Box sx={{ pb: 0.5 }}>
      {/* Manual saved (not yet live) synthetics — the “top pane” listing. The ⌖ toggle
          scopes the list to the SELECTED underlying (default) vs all saved symbols. */}
      <Section title="Synthetic · saved"
        extra={
          <Tooltip arrow title={savedScoped
            ? `Showing only ${underlyingSymbol || 'the selected underlying'}'s saved synthetics — click to show ALL`
            : 'Showing ALL saved synthetics — click to scope to the selected underlying'}>
            <Chip size="small" clickable label={savedScoped ? (underlyingSymbol || '—') : 'ALL'}
              onClick={() => toggleSavedScope()}
              color={savedScoped ? 'primary' : 'default'} variant={savedScoped ? 'filled' : 'outlined'}
              sx={{ height: 18, fontSize: CH_TYPE.micro, fontWeight: 800, '& .MuiChip-label': { px: 0.5 } }} />
          </Tooltip>
        }>
        {(savedScoped ? manual.filter(r => (r as any).underlying?.symbol === underlyingSymbol) : manual).length
          ? (savedScoped ? manual.filter(r => (r as any).underlying?.symbol === underlyingSymbol) : manual).map(r => (
            <Row key={r.id} row={r} active={activeSyntheticId === r.id} onClick={() => onOpen(r)} onContext={onContext} onRemove={removeRow} />
          ))
          : empty(savedScoped
            ? `None saved for ${underlyingSymbol || 'this underlying'} — build a basket and “Save as symbol”, or click the chip to show ALL.`
            : 'None — build a basket on the chart and “Save as symbol”.')}
      </Section>

      {/* Pane 1 — LIVE positions (any chip type). */}
      <Section title="Synthetic · live">
        {live.length ? live.map(r => (
          <Row key={r.id} row={r} active={activeSyntheticId === r.id} onClick={() => onOpen(r)} onContext={onContext} />
        )) : empty('No live synthetic positions.')}
      </Section>

      {/* Pane 2 — AUTO-generated for the selected underlying. */}
      <Section
        title={`Auto${underlyingSymbol ? ` · ${underlyingSymbol}` : ''}`}
        extra={
          <Tooltip arrow title="Recompute the auto set for this underlying (4 directional chips × D0/D1/D2/W0/M0) — replaces the current un-armed rows. Regenerates itself at day rollover too.">
            <span>
              <IconButton size="small" onClick={doRefreshAuto} disabled={!underlyingSymbol || refreshing} sx={{ p: 0.2 }}>
                {refreshing ? <CircularProgress size={11} /> : <RefreshIcon sx={{ fontSize: 13 }} />}
              </IconButton>
            </span>
          </Tooltip>
        }>
        {!underlyingSymbol
          ? empty('Select an optionable symbol to see auto synthetics.')
          : auto.length
            ? BUCKETS.filter(b => autoByBucket.has(b)).map(b => {
                const rows = autoByBucket.get(b)!;
                const collapsed = !!autoCollapsed[b];
                return (
                  <Box key={b}>
                    {/* Bucket header — collapsible D0/D1/D2/W0/M0 set. */}
                    <Box onClick={() => toggleBucket(b)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.6, py: 0.15, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                      {collapsed ? <ChevronRightIcon sx={{ fontSize: 13, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 13, color: 'text.secondary' }} />}
                      <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 800, letterSpacing: 0.4, color: 'text.secondary' }}>
                        {b} ({rows.length})
                      </Typography>
                    </Box>
                    <Collapse in={!collapsed} unmountOnExit>
                      {rows.map(r => <Row key={r.id} row={r} active={activeSyntheticId === r.id} onClick={() => onOpen(r)} onContext={onContext} onRemove={removeRow} />)}
                    </Collapse>
                  </Box>
                );
              })
            : empty('None yet — hit ⟳ to generate for this underlying.')}

        {/* Bhairav_Scanner matches — flat list ASCENDING by expiry (soonest first),
            cross-underlying, each with a time-to-expiry chip. Click → chart → arm a
            trigger manually (the scanner never trades). */}
        {scanRows.length > 0 && (
          <Box sx={{ mt: 0.4 }}>
            <Box onClick={toggleScanner}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.6, py: 0.15, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
              {scannerCollapsed ? <ChevronRightIcon sx={{ fontSize: CH_TYPE.body, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: CH_TYPE.body, color: 'text.secondary' }} />}
              <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 800, letterSpacing: 0.4, color: 'text.secondary' }}>
                SCANNER ({scanRows.length})
              </Typography>
              <Tooltip arrow title="Bhairav_Scanner matches — strangle/straddle combinations whose combined premium printed its first drop signal. Sorted by expiry (soonest first). Open the chart and arm a SHORT trigger if the setup looks good. Click the header to collapse.">
                <Typography component="span" sx={{ fontSize: CH_TYPE.micro, color: 'text.disabled', cursor: 'help' }} onClick={e => e.stopPropagation()}>ⓘ</Typography>
              </Tooltip>
            </Box>
            <Collapse in={!scannerCollapsed}>
            <Box sx={{ maxHeight: 176, overflowY: 'auto' }}>
            {scanRows.map(r => {
              const eta = r.nearestLegExpiryMs != null ? Number(r.nearestLegExpiryMs) - Date.now() : null;
              const etaTxt = eta == null ? '' : eta <= 0 ? 'exp'
                : eta >= 48 * 3600_000 ? `${Math.floor(eta / 86400_000)}d ${Math.floor((eta % 86400_000) / 3600_000)}h`
                : eta >= 3600_000 ? `${Math.floor(eta / 3600_000)}h ${Math.floor((eta % 3600_000) / 60_000)}m`
                : `${Math.max(1, Math.floor(eta / 60_000))}m`;
              const si = r.scanInfo;
              return (
                <Box key={r.id} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Row row={r} active={activeSyntheticId === r.id} onClick={() => onOpen(r)} onContext={onContext} onRemove={removeRow} />
                  </Box>
                  {/* Which engine flagged it, on which timeframe (provenance). */}
                  {si && (
                    <Tooltip arrow title={`${si.label}: ${si.signal} @ ${si.timeframeMin}m${si.candleType ? ` ${si.candleType}` : ''} — ${new Date(si.at).toLocaleString()}`}>
                      <Chip size="small" label={`${si.label === 'TM_Ultra' ? 'TMU' : 'BHRV'}·${si.timeframeMin}m`}
                        sx={{ height: 18, fontSize: CH_TYPE.micro, fontWeight: 800, color: si.label === 'TM_Ultra' ? '#7e57c2' : '#ef6c00', bgcolor: 'transparent', border: '1px solid', borderColor: 'divider', '& .MuiChip-label': { px: 0.35 } }} />
                    </Tooltip>
                  )}
                  {etaTxt && (
                    <Tooltip arrow title="Time to the combination's expiry settlement">
                      <Chip size="small" label={etaTxt}
                        sx={{ height: 18, mr: 0.5, ml: 0.3, fontSize: CH_TYPE.micro, fontWeight: 700, color: eta != null && eta < 12 * 3600_000 ? '#ef6c00' : 'text.secondary', bgcolor: 'transparent', border: '1px solid', borderColor: 'divider', '& .MuiChip-label': { px: 0.4 } }} />
                    </Tooltip>
                  )}
                </Box>
              );
            })}
            </Box>
            </Collapse>
          </Box>
        )}
      </Section>

      {/* Right-click context menu */}
      <Menu
        open={!!ctx}
        onClose={() => setCtx(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctx ? ctx.anchor : undefined}
      >
        {ctx?.row.status === 'ARMED' && (
          <MenuItem dense onClick={doDisarm} sx={{ fontSize: CH_TYPE.body }}>Disarm trigger</MenuItem>
        )}
        <MenuItem
          dense
          onClick={doDelete}
          disabled={ctx?.row.status === 'LIVE' || ctx?.row.status === 'LIVE_PARTIAL'}
          sx={{ fontSize: CH_TYPE.body, color: '#c62828' }}
        >
          {ctx?.row.status === 'LIVE' || ctx?.row.status === 'LIVE_PARTIAL' ? 'Delete (close the position first)' : 'Delete'}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SyntheticSymbolPanes;
